import React, { useState } from 'react';
import { Link, NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  LayoutGrid, Users, Receipt, GraduationCap, BookMarked, FileBarChart,
  Settings, ShieldCheck, History, LogOut, Menu, X,
} from 'lucide-react';
import { ApcMark } from './ApcMark';
import { useAuth } from '../lib/auth-context';

const Item = ({ to, icon: Icon, label, testid }) => (
  <NavLink
    to={to}
    end
    className={({ isActive }) =>
      `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${
        isActive ? 'bg-[#1F6F4A] text-white' : 'text-[#1B2A41] hover:bg-[#F3F5F2]'
      }`
    }
    data-testid={testid}
  >
    <Icon size={16} /> {label}
  </NavLink>
);

const AdminShell = ({ children, title }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const doLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const nav = (
    <>
      <Item to="/admin" icon={LayoutGrid} label="Dashboard" testid="nav-dashboard"/>
      <Item to="/admin/learners" icon={Users} label="Learners" testid="nav-learners"/>
      <Item to="/admin/payments" icon={Receipt} label="Payments" testid="nav-payments"/>
      <Item to="/admin/onboarding" icon={GraduationCap} label="Onboarding" testid="nav-onboarding"/>
      <Item to="/admin/catalog" icon={BookMarked} label="Catalog" testid="nav-catalog"/>
      <Item to="/admin/reports" icon={FileBarChart} label="Reports" testid="nav-reports"/>
      {user?.role === 'OWNER' && <>
        <div className="pt-3 mt-3 border-t border-black/5"/>
        <Item to="/admin/admins" icon={ShieldCheck} label="Admins" testid="nav-admins"/>
        <Item to="/admin/settings" icon={Settings} label="Settings" testid="nav-settings"/>
        <Item to="/admin/audit" icon={History} label="Audit log" testid="nav-audit"/>
      </>}
    </>
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6]" data-testid="admin-shell">
      <div className="flex min-h-screen">
        {/* Sidebar (desktop) */}
        <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-black/5 bg-white p-5">
          <Link to="/admin" className="flex items-center gap-2 mb-7">
            <ApcMark size={30}/>
            <span className="font-bold text-[#1B2A41]" style={{ fontFamily: 'Outfit' }}>APC Admin</span>
          </Link>
          <nav className="flex flex-col gap-1 flex-1">{nav}</nav>
          <div className="mt-6 border-t border-black/5 pt-4">
            <p className="text-xs text-[#4B5563] mb-1" data-testid="current-user-email">{user?.email}</p>
            <p className="text-xs font-mono tracking-wider text-[#D4A017]" data-testid="current-user-role">{user?.role}</p>
            <button onClick={doLogout} className="mt-3 text-sm text-[#1B2A41] hover:text-[#1F6F4A] flex items-center gap-2" data-testid="logout-btn"><LogOut size={14}/> Sign out</button>
          </div>
        </aside>

        {/* Mobile top bar */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-white border-b border-black/5 flex items-center justify-between px-4 h-14">
          <Link to="/admin" className="flex items-center gap-2"><ApcMark size={26}/><span className="font-bold text-sm" style={{ fontFamily: 'Outfit' }}>APC Admin</span></Link>
          <button onClick={() => setOpen(!open)} data-testid="mobile-admin-toggle">{open ? <X size={20}/> : <Menu size={20}/>}</button>
        </div>
        {open && (
          <div className="lg:hidden fixed inset-0 top-14 bg-white z-30 p-5" data-testid="mobile-admin-menu">
            <nav className="flex flex-col gap-1">{nav}</nav>
            <button onClick={doLogout} className="mt-6 text-sm text-[#1B2A41] flex items-center gap-2"><LogOut size={14}/> Sign out</button>
          </div>
        )}

        {/* Main */}
        <main className="flex-1 min-w-0 pt-16 lg:pt-0">
          <div className="p-5 md:p-8 max-w-7xl mx-auto">
            <h1 className="text-2xl md:text-3xl font-bold text-[#1B2A41] mb-6" style={{ fontFamily: 'Outfit' }} data-testid="admin-page-title">{title}</h1>
            {children}
          </div>
        </main>
      </div>
    </div>
  );
};

export default AdminShell;
